import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  IconButton,
  Grid,
  Tabs,
  Tab,
  Alert,
  Snackbar,
  Chip,
  useTheme,
  Paper,
  Stack,
  LinearProgress
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Assessment as ReportIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import AuthService from '../../services/AuthService';
import { useSubscription } from '../../hooks/useSubscription';
import dayjs from 'dayjs';

// Import the extracted components
import MappingSessionSidebar from './components/MappingSessionSidebar';
import { MappingFormDialog, FollowupFormDialog } from './components/MappingDialogs';
import { AddAnnotationDialog, EditAnnotationDialog, ReplaceImageDialog, ReplaceMacroDialog } from './components/AnnotationDialogs';
import DermatoscopicPanel from './components/DermatoscopicPanel';
import MacroImageViewer from './components/MacroImageViewer';
import MacroImageGallery from './components/MacroImageGallery';
import CameraDialog, { CameraDialogRef } from './components/CameraDialog';
import BaselineComparisonDialog from './components/BaselineComparisonDialog';
import BaselineComparisonPanel from './components/BaselineComparisonPanel';

// Import shared types
import { MoleMappingData, MacroImage, Annotation, ImageBounds, BaselineAnnotationContext } from './types';

import { 
  createMoleMapping, 
  createFollowupMoleMapping,
  getMoleMapping, 
  getPatientMoleMappings,
  getPatientBaselineSessions,
  updateMoleMapping, 
  deleteMoleMapping, 
  addAnnotation,
  getMacroImages,
  getMappingAnnotations,
  updateAnnotation,
  deleteAnnotation,
  uploadMicroImage,
  linkMicroToAnnotation,
  deleteMicroImage,
  deleteMacroImage,
  AnnotationPayload
} from '../../services/moleMappingApi';
import { useSSE } from './hooks/useSSE';
import { useNotifications } from '../../api/common/components/NotificationContext';
import BaselineAnnotationGuide from './components/BaselineAnnotationGuide';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const MoleMappingSpace: React.FC = () => {
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const { addNotification } = useNotifications();
  const subscription = useSubscription();
  
  
  // User ID from auth
  const [userId, setUserId] = useState<string>('');
  
  // Patient data
  const [patientName, setPatientName] = useState<string>('');
  
  // Mole mapping session states
  const [mappings, setMappings] = useState<MoleMappingData[]>([]);
  const [selectedMapping, setSelectedMapping] = useState<MoleMappingData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  // Image states
  const [macroImages, setMacroImages] = useState<MacroImage[]>([]);
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [selectedMacroImage, setSelectedMacroImage] = useState<MacroImage | null>(null);
  const [selectedAnnotation, setSelectedAnnotation] = useState<Annotation | null>(null);
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
  
  // UI states
  const [annotationMode, setAnnotationMode] = useState<boolean>(false);
  const [currentCoordinates, setCurrentCoordinates] = useState<{x: number, y: number, width: number, height: number} | null>(null);
  const [showAnnotationForm, setShowAnnotationForm] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);

  // Image dimensions for calculations
  const [imageNaturalDimensions, setImageNaturalDimensions] = useState<{width: number, height: number} | null>(null);
  const [imageDisplayDimensions, setImageDisplayDimensions] = useState<{width: number, height: number} | null>(null);
  
  // Image upload and annotation refs
  const macroImageRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const microFileInputRef = useRef<HTMLInputElement>(null);
  
  // Form states
  const [mappingForm, setMappingForm] = useState({
    sessionDate: dayjs().format('YYYY-MM-DD'),
    notes: ''
  });
  const [showMappingForm, setShowMappingForm] = useState<boolean>(false);
  const [annotationForm, setAnnotationForm] = useState({
    label: '',
    orderIndex: 0
  });
  
  // Annotation editing states
  const [editingAnnotation, setEditingAnnotation] = useState<Annotation | null>(null);
  const [showEditAnnotationForm, setShowEditAnnotationForm] = useState<boolean>(false);
  const [editAnnotationForm, setEditAnnotationForm] = useState({
    label: '',
    orderIndex: 0
  });
  
  const [uploadingMicroImage, setUploadingMicroImage] = useState<boolean>(false);
  
  // Replacement confirmation states
  const [showReplaceConfirmDialog, setShowReplaceConfirmDialog] = useState<boolean>(false);
  const [replacementFile, setReplacementFile] = useState<File | null>(null);
  
  // Macro image replacement states
  const [showReplaceMacroConfirmDialog, setShowReplaceMacroConfirmDialog] = useState<boolean>(false);
  const [replacementMacroFile, setReplacementMacroFile] = useState<File | null>(null);
  const [macroToReplace, setMacroToReplace] = useState<MacroImage | null>(null);
  
  // Drag and drop states
  const [isDragActive, setIsDragActive] = useState<boolean>(false);
  
  // Baseline comparison states
  const [showBaselineComparison, setShowBaselineComparison] = useState<boolean>(false);
  const [baselineContext, setBaselineContext] = useState<BaselineAnnotationContext | null>(null);
  const [baselineRefreshTrigger, setBaselineRefreshTrigger] = useState<number>(0);
  const [isFollowupSession, setIsFollowupSession] = useState<boolean>(false);
  const [showFollowupDialog, setShowFollowupDialog] = useState<boolean>(false);
  const [baselineSessions, setBaselineSessions] = useState<any[]>([]);
  const [selectedBaselineForFollowup, setSelectedBaselineForFollowup] = useState<string>('');
  const [followupForm, setFollowupForm] = useState({
    sessionDate: dayjs().format('YYYY-MM-DD'),
    notes: '',
    intervalMonths: 6
  });
  
  // Camera states
  const [cameraOpen, setCameraOpen] = useState<boolean>(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [cameraLoading, setCameraLoading] = useState<boolean>(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [frontCamera, setFrontCamera] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [cameraInitAttempts, setCameraInitAttempts] = useState<number>(0);
  const [availableCameras, setAvailableCameras] = useState<MediaDeviceInfo[]>([]);
  const [currentCameraIndex, setCurrentCameraIndex] = useState<number>(0);
  
  // SSE connection state
  const [sseConnected, setSseConnected] = useState<boolean>(false);
  
  // Refs for camera functionality
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const cameraDialogRef = useRef<CameraDialogRef>(null);
  
  // Check if user is on mobile device
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      setIsMobile(
        /android|webos|iphone|ipad|ipod|blackberry|windows phone/.test(userAgent)
      );
    };
    
    checkMobile();
  }, []);
  
  // Get user ID on mount
  useEffect(() => {
    const fetchUserId = async () => {
      const authService = new AuthService();
      try {
        const user = await authService.getUser();
        if (user?.profile?.sub) {
          setUserId(user.profile.sub);
        }
      } catch (error) {
        console.error("Failed to get user ID:", error);
        setError("Authentication error. Please sign in again.");
      }
    };
    
    fetchUserId();
    
    // Get patient name - this would be from a real API in production
    if (patientId) {
      // Mock implementation - would be replaced with API call
      setPatientName(`Patient ${patientId}`);
    }
  }, [patientId]);
  
  // Load patient mole mapping sessions
  useEffect(() => {
    if (patientId) {
      fetchMoleMappings();
    }
  }, [patientId]);
  
  // Fetch mole mapping sessions for the patient
  const fetchMoleMappings = async () => {
    if (!patientId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const data = await getPatientMoleMappings(patientId);
      setMappings(Array.isArray(data) ? data : []); // Ensure we always have an array
      
      // If there is at least one mapping, select the most recent one
      if (data && data.length > 0) {
        const mostRecent = data.reduce((prev:any, current:any) => {
          return new Date(prev.createdAt) > new Date(current.createdAt) ? prev : current;
        });
        setSelectedMapping(mostRecent);
        await loadMappingData(mostRecent.id);
      } else {
        // No existing mappings - this is normal for new patients
        setSelectedMapping(null);
        setMacroImages([]);
        setSelectedMacroImage(null);
        setAnnotations([]);
      }
    } catch (error:any) {
      console.error("Failed to fetch mole mappings:", error);
      
      // More specific error handling
      if (error.response?.status === 404 || error.response?.status === 204) {
        // No mappings found for this patient - this is not an error
        setMappings([]);
        setSelectedMapping(null);
      } else if (error.response?.status === 401 || error.response?.status === 403) {
        setError("Authentication failed. Please sign in again.");
      } else if (error.code === 'NETWORK_ERROR' || error.message?.includes('Network Error')) {
        setError("Network connection error. Please check your internet connection and try again.");
      } else {
        setError("Failed to load mole mapping sessions. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Load data for a selected mapping
  const loadMappingData = async (mappingId: string) => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch the mapping data
      const mappingData = await getMoleMapping(mappingId);
      setSelectedMapping(mappingData);
      
      // Fetch macro images for this mapping
      const macroImagesData = await getMacroImages(mappingId);
      setMacroImages(macroImagesData);
      
      // If there's at least one macro image, select it and load its annotations
      if (macroImagesData.length > 0) {
        const firstImage = macroImagesData[0];
        setSelectedMacroImage(firstImage);
        
        // Load annotations for this image
        const annotationsData = await getMappingAnnotations(firstImage.id);
        setAnnotations(annotationsData);
      } else {
        setSelectedMacroImage(null);
        setAnnotations([]);
      }
    } catch (error:any) {
      console.error("Failed to load mapping data:", error);
      if (error.response?.status === 404) {
        setError("Mole mapping session not found.");
      } else if (error.response?.status === 401) {
        setError("Authentication failed. Please sign in again.");
      } else {
        setError("Failed to load mole mapping data. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Create new mole mapping session
  const handleCreateMapping = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const sessionDate = mappingForm.sessionDate || dayjs().format('YYYY-MM-DD');
      const notes = mappingForm.notes || '';
      
      if (!patientId || !userId) {
        throw new Error("Missing patient ID or user ID");
      }
      
      // Create with empty file array - images can be added later
      const emptyFileArray: File[] = [];
      
      console.log("Creating mole mapping with:", { patientId, userId, sessionDate, notes });
      
      const response = await createMoleMapping(
        patientId,
        userId,
        sessionDate,
        notes,
        emptyFileArray
      );
      
      console.log("Mole mapping creation response:", response);
      
      // Add the new mapping to our list and select it
      const newMapping: MoleMappingData = {
        id: response.mappingId,
        patientId,
        userId,
        sessionDate,
        notes,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      setMappings([newMapping, ...mappings]);
      setSelectedMapping(newMapping);
      setMacroImages([]);
      setSelectedMacroImage(null);
      setAnnotations([]);
      
      // Reset form and close dialog
      setMappingForm({
        sessionDate: dayjs().format('YYYY-MM-DD'),
        notes: ''
      });
      setShowMappingForm(false);
      
      // Show success message
      setSnackbarMessage("Mole mapping session created successfully");
      setSnackbarOpen(true);
      
    } catch (error:any) {
      console.error("Failed to create mole mapping:", error);
      
      let errorMessage = "Failed to create mole mapping session.";
      
      if (error.response?.status === 401 || error.response?.status === 403) {
        errorMessage = "Authentication failed. Please sign in again.";
      } else if (error.response?.status === 422) {
        errorMessage = "Invalid data provided. Please check your inputs and try again.";
      } else if (error.response?.data?.detail) {
        errorMessage = `Error: ${error.response.data.detail}`;
      } else if (error.code === 'NETWORK_ERROR' || error.message?.includes('Network Error')) {
        errorMessage = "Network connection error. Please check your internet connection.";
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle macro image upload
  const handleMacroImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0 || !selectedMapping) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const files = Array.from(event.target.files);
      
      // Validate files
      const validFiles = files.filter(file => {
        if (!file.type.startsWith('image/')) {
          setSnackbarMessage(`Skipping ${file.name}: Not an image file`);
          setSnackbarOpen(true);
          return false;
        }
        if (file.size > 50 * 1024 * 1024) { // 50MB limit
          setSnackbarMessage(`Skipping ${file.name}: File too large (max 50MB)`);
          setSnackbarOpen(true);
          return false;
        }
        return true;
      });

      if (validFiles.length === 0) {
        setError("No valid image files to upload");
        return;
      }
      
      console.log(`Uploading ${validFiles.length} files for mapping: ${selectedMapping.id}`);
      
      // Update the mapping with the new images
      const notes = selectedMapping.notes || '';
      
      const response = await updateMoleMapping(selectedMapping.id, notes, validFiles);
      
      console.log('Upload response:', response);
      
      if (response?.success) {
      // Refresh the macro images for this mapping
      const macroImagesData = await getMacroImages(selectedMapping.id);
      setMacroImages(macroImagesData);
      
      // Select the newly uploaded image if there are new images
      if (response.addedImageIds && response.addedImageIds.length > 0 && macroImagesData.length > 0) {
        // Find the newly added image
        const newImage = macroImagesData.find((img: MacroImage) => 
          response.addedImageIds.includes(img.id)
        );
        
        if (newImage) {
          setSelectedMacroImage(newImage);
          // Clear annotations as this is a new image
          setAnnotations([]);
        } else {
          // Fallback: select the last image
          const lastImage = macroImagesData[macroImagesData.length - 1];
          setSelectedMacroImage(lastImage);
          setAnnotations([]);
        }
      }
        
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        
        // Show success message
        const uploadCount = response.addedImageIds?.length || validFiles.length;
        setSnackbarMessage(`${uploadCount} image(s) uploaded successfully`);
        setSnackbarOpen(true);
        
        // Refresh usage after upload
        if (subscription.refreshUsage) {
          subscription.refreshUsage();
        }
      } else {
        throw new Error('Upload response indicates failure');
      }
      
    } catch (error: any) {
      console.error("Failed to upload macro image:", error);
      console.error("Error details:", error.response?.data);
      
      let errorMessage = "Failed to upload macro image. Please try again.";
      
      if (error.response?.status === 403) {
        errorMessage = "You've reached the free tier limit. Please upgrade your plan to continue.";
        setError(errorMessage);
        // Refresh usage to show updated count
        if (subscription.refreshUsage) {
          subscription.refreshUsage();
        }
        // Optionally navigate to subscription page after a delay
        setTimeout(() => {
          navigate('/account?tab=subscription');
        }, 2000);
        return;
      } else if (error.response?.status === 413) {
        errorMessage = "File too large. Please choose a smaller image.";
      } else if (error.response?.status === 415) {
        errorMessage = "Unsupported file type. Please upload an image file.";
      } else if (error.response?.status === 422) {
        errorMessage = "Invalid file data. Please check the file and try again.";
      } else if (error.response?.data?.detail) {
        errorMessage = `Upload failed: ${error.response.data.detail}`;
      } else if (error.code === 'NETWORK_ERROR' || error.message?.includes('Network Error')) {
        errorMessage = "Network error. Please check your connection and try again.";
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Drag and drop handlers
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!loading) {
      setIsDragActive(true);
    }
  }, [loading]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!loading) {
      setIsDragActive(true);
    }
  }, [loading]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    
    if (!loading && e.dataTransfer.files && e.dataTransfer.files.length > 0 && selectedMapping) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.type.startsWith('image/')) {
        // Create a custom file input event to reuse existing upload logic
        const fileChangeEvent = {
          target: {
            files: e.dataTransfer.files
          }
        } as React.ChangeEvent<HTMLInputElement>;
        
        handleMacroImageUpload(fileChangeEvent);
      } else {
        setSnackbarMessage("Please upload an image file.");
        setSnackbarOpen(true);
      }
    }
  }, [loading, selectedMapping]);
  
  // Check and enumerate available cameras
  const enumerateCameras = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
        console.log("enumerateDevices() not supported");
        return [];
      }

      const devices = await navigator.mediaDevices.enumerateDevices();
      const cameras = devices.filter(device => device.kind === 'videoinput');
      console.log("Available cameras:", cameras);
      setAvailableCameras(cameras);
      return cameras;
    } catch (err) {
      console.error("Error enumerating devices:", err);
      return [];
    }
  };
  
  // Camera handlers
  const openCamera = async () => {
    try {
      setCameraLoading(true);
      setCameraError(null);
      setCameraOpen(true); // Open dialog immediately to show loading state

      // Get available cameras first
      const cameras = await enumerateCameras();
      
      // Determine which camera to use
      let constraints: MediaStreamConstraints = { audio: false, video: true };
      
      if (cameras.length > 0) {
        // If we have specific device IDs, use them
        const cameraToUse = cameras[currentCameraIndex];
        if (cameraToUse?.deviceId) {
          constraints = {
            audio: false,
            video: {
              deviceId: { exact: cameraToUse.deviceId },
              width: { ideal: 1280 },
              height: { ideal: 720 }
            }
          };
          console.log(`Using camera ${currentCameraIndex + 1}/${cameras.length} with ID: ${cameraToUse.deviceId}`);
        }
      } else {
        // Fall back to facingMode if no device IDs are available
        const preferredFacingMode = isMobile ? "environment" : "user";
        constraints = {
          audio: false,
          video: {
            facingMode: frontCamera ? "user" : preferredFacingMode,
            width: { ideal: 1280 },
            height: { ideal: 720 }
          }
        };
        console.log("Using facingMode constraint:", constraints.video);
      }
      
      console.log(`Camera init attempt ${cameraInitAttempts + 1} with constraints:`, constraints);
      
      // For Safari compatibility, explicitly set timeouts
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error("Camera access timeout")), 10000);
      });
      
      // Try to get the media stream with timeout
      const stream = await Promise.race([
        navigator.mediaDevices.getUserMedia(constraints),
        timeoutPromise
      ]) as MediaStream;
      
      // If we reach here, we have a stream
      setCameraStream(stream);
      
      if (videoRef.current) {
        // In some browsers, setting srcObject can be asynchronous
        videoRef.current.srcObject = stream;
        
        // Force a reload of the video element
        videoRef.current.load();
        
        // Add event listeners to handle when video is ready
        videoRef.current.onloadedmetadata = () => {
          console.log("Video metadata loaded");
          if (videoRef.current) {
            videoRef.current.play()
              .then(() => {
                console.log("Video playback started");
                setCameraLoading(false);
                setCameraInitAttempts(0); // Reset attempts on success
              })
              .catch(err => {
                console.error("Error playing video:", err);
                setCameraError("Failed to start video playback. Please try Switch Camera.");
                setCameraLoading(false);
              });
          }
        };
        
        videoRef.current.onerror = () => {
          console.error("Video element error");
          setCameraError("Video element encountered an error. Please try Switch Camera.");
          setCameraLoading(false);
        };
        
        // Set a timeout in case the onloadedmetadata event never fires
        setTimeout(() => {
          if (cameraLoading) {
            console.log("Camera init timed out - attempting to force play");
            if (videoRef.current) {
              videoRef.current.play()
                .then(() => {
                  console.log("Forced video playback started");
                  setCameraLoading(false);
                })
                .catch(err => {
                  console.error("Forced play error:", err);
                  // Don't set error here as we'll try the fallback
                  tryFallbackInitialization();
                });
            }
          }
        }, 3000);
      }
    } catch (err) {
      console.error("Error accessing camera:", err);
      let errorMessage = "Could not access camera. Please try the Switch Camera button or check permissions.";
      
      // More specific error messages based on the error
      if (err instanceof DOMException) {
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          errorMessage = "Camera access was denied. Please allow camera permissions in your browser settings.";
        } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
          errorMessage = "No camera found on your device or it's being used by another application.";
        } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
          errorMessage = "Camera is in use by another application or is not accessible.";
        } else if (err.name === 'OverconstrainedError') {
          errorMessage = "Camera doesn't support the requested settings. Try the Switch Camera button.";
        } else if (err.name === 'AbortError') {
          errorMessage = "Camera access was aborted. Please try again or use Switch Camera.";
        }
      }
      
      setCameraError(errorMessage);
      setCameraLoading(false);
      
      // If this is the first attempt, try a fallback
      if (cameraInitAttempts < 2) {
        tryFallbackInitialization();
      }
    }
  };

  // Fallback camera initialization with simpler constraints
  const tryFallbackInitialization = () => {
    setCameraInitAttempts(prev => prev + 1);
    setTimeout(() => {
      console.log("Trying fallback camera initialization");
      // Toggle frontCamera which will trigger different constraints
      setFrontCamera(prev => !prev);
      // Re-attempt with new settings after a short delay
      setTimeout(() => {
        if (cameraOpen) {
          switchCamera();
        }
      }, 500);
    }, 1000);
  };

  const switchCamera = async () => {
    try {
      setCameraLoading(true);
      setCameraError(null); // Clear any existing errors
      
      // Close existing stream
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => {
          try {
            track.stop();
          } catch (e) {
            console.error("Error stopping track:", e);
          }
        });
        setCameraStream(null);
      }
      
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
      
      // Refresh camera list
      const cameras = await enumerateCameras();
      
      if (cameras.length > 1) {
        // If we have multiple cameras, cycle through them
        const newIndex = (currentCameraIndex + 1) % cameras.length;
        console.log(`Switching to camera ${newIndex + 1}/${cameras.length}`);
        setCurrentCameraIndex(newIndex);
      } else {
        // If we don't have specific cameras enumerated, just toggle facingMode
        console.log("Toggling between front and rear camera");
        setFrontCamera(prev => !prev);
      }
      
      // Small delay before reopening camera with new settings
      setTimeout(() => {
        // Create new constraints based on updated camera selection
        let constraints: MediaStreamConstraints = { audio: false, video: true };
        
        if (cameras.length > 0) {
          // If we have specific device IDs, use the next one in the list
          const nextCameraIndex = (currentCameraIndex + 1) % cameras.length;
          const cameraToUse = cameras[nextCameraIndex];
          
          if (cameraToUse?.deviceId) {
            constraints = {
              audio: false,
              video: {
                deviceId: { exact: cameraToUse.deviceId },
                width: { ideal: 1280 },
                height: { ideal: 720 }
              }
            };
            console.log(`Using new camera with ID: ${cameraToUse.deviceId}`);
          }
        } else {
          // Fall back to facingMode if no device IDs are available
          const newFacingMode = !frontCamera;
          constraints = {
            audio: false,
            video: {
              facingMode: newFacingMode ? "user" : "environment",
              width: { ideal: 1280 },
              height: { ideal: 720 }
            }
          };
          console.log(`Using facingMode constraint: ${newFacingMode ? "user" : "environment"}`);
        }
        
        // Get the new stream with the updated constraints
        navigator.mediaDevices.getUserMedia(constraints)
          .then(stream => {
            // Set the new stream
            setCameraStream(stream);
            
            if (videoRef.current) {
              videoRef.current.srcObject = stream;
              videoRef.current.play()
                .then(() => {
                  console.log("New camera started successfully");
                  setCameraLoading(false);
                })
                .catch(err => {
                  console.error("Error starting new camera:", err);
                  setCameraError("Failed to start video with new camera. Please try another one.");
                  setCameraLoading(false);
                });
            }
          })
          .catch(err => {
            console.error("Error getting new media stream:", err);
            setCameraError("Failed to access the new camera. Please check permissions or try another one.");
            setCameraLoading(false);
          });
      }, 300);
    } catch (err) {
      console.error("Error switching camera:", err);
      setCameraError("Failed to switch camera. Please try again or use file upload instead.");
      setCameraLoading(false);
    }
  };

  const closeCamera = () => {
    // Stop all tracks from the camera stream
    if (cameraStream) {
      try {
        // Stop all tracks individually
        cameraStream.getTracks().forEach(track => {
          track.stop();
          console.log(`Stopped camera track: ${track.kind}`);
        });
        
        // Clear the stream reference
        setCameraStream(null);
      } catch (err) {
        console.error("Error stopping camera tracks:", err);
      }
    }
    
    // Clear the video source
    if (videoRef.current) {
      try {
        // Remove the srcObject to fully disconnect
        videoRef.current.srcObject = null;
        console.log("Cleared video source object");
      } catch (err) {
        console.error("Error clearing video source:", err);
      }
    }
    
    // Reset camera states
    setCameraOpen(false);
    setCameraLoading(false);
    setCameraError(null);
    
    // Log camera cleanup for debugging
    console.log("Camera resources released");
  };

  const captureImage = (isMicroImage: boolean = false) => {
    if (videoRef.current && canvasRef.current && selectedMapping) {
      // Try to use zoomed capture first if available
      if (cameraDialogRef.current) {
        console.log('Using zoomed capture from camera dialog');
        const zoomedDataUrl = cameraDialogRef.current.captureZoomedImage();
        
        if (zoomedDataUrl) {
          // Convert data URL to blob and then to file
          fetch(zoomedDataUrl)
            .then(res => res.blob())
            .then(blob => {
              const fileName = `camera_capture_${Date.now()}.jpg`;
              const file = new File([blob], fileName, { type: 'image/jpeg' });
              
              // Close camera before proceeding with upload
              closeCamera();
              if (isMicroImage && selectedAnnotation?.linkedMicroId && !baselineContext) {
              setReplacementFile(file);
              setShowReplaceConfirmDialog(true);
              return;
            }
              if (isMicroImage && selectedAnnotation) {
                // For micro images (dermatoscopic), handle via the micro image upload process
                performMicroImageUpload(file);
              } else {
                // For macro images, use the existing upload handler
                const files = [file];
                const customEvent = {
                  target: {
                    files: {
                      0: file,
                      length: 1,
                      item: (index: number) => files[index]
                    }
                  }
                } as unknown as React.ChangeEvent<HTMLInputElement>;
                handleMacroImageUpload(customEvent);
              }
            })
            .catch(error => {
              console.error('Error converting zoomed capture to file:', error);
              // Fallback to regular capture
              fallbackCapture();
            });
          return; // Exit early since we're using zoomed capture
        }
      }
      
      // Fallback to regular capture if zoomed capture fails
      fallbackCapture();
    }
    
    function fallbackCapture() {
      if (!videoRef.current || !canvasRef.current) return;
      
      const video = videoRef.current;
      const canvas = canvasRef.current;
      
      // Set canvas dimensions to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      // Draw video frame to canvas
      const context = canvas.getContext('2d');
      if (context) {
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        // Convert to file
        canvas.toBlob((blob) => {
          if (blob) {
            const fileName = `camera_capture_${Date.now()}.jpg`;
            const file = new File([blob], fileName, { type: 'image/jpeg' });
            
            // Close camera before proceeding with upload
            closeCamera();
            
            if (isMicroImage && selectedAnnotation) {
              // For micro images (dermatoscopic), handle via the micro image upload process
              performMicroImageUpload(file);
            } else {
              // For macro images, use the existing upload handler
              const files = [file];
              const customEvent = {
                target: {
                  files: {
                    0: file,
                    length: 1,
                    item: (index: number) => files[index]
                  }
                }
              } as unknown as React.ChangeEvent<HTMLInputElement>;
              handleMacroImageUpload(customEvent);
            }
          }
        }, 'image/jpeg', 0.9); // Use 90% quality for JPEG
      }
    }
  };

  // Clean up camera resources when component unmounts
  useEffect(() => {
    return () => {
      // Clean up camera resources when component unmounts
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [cameraStream]);

  // Edit an annotation
  const handleEditAnnotation = (annotation: Annotation) => {
    setEditingAnnotation(annotation);
    setEditAnnotationForm({
      label: annotation.label,
      orderIndex: annotation.orderIndex
    });
    setShowEditAnnotationForm(true);
  };

  // Save edited annotation
  const handleSaveEditedAnnotation = async () => {
    if (!editingAnnotation) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const updates = {
        label: editAnnotationForm.label,
        orderIndex: editAnnotationForm.orderIndex,
        updatedAt: format_timestamp_for_weaviate()
      };
      
      await updateAnnotation(editingAnnotation.id, updates);
      
      // Update the annotation in our list
      const updatedAnnotations = annotations.map(a => 
        a.id === editingAnnotation.id 
          ? { ...a, ...updates, updatedAt: new Date().toISOString() }
          : a
      );
      setAnnotations(updatedAnnotations);
      
      // If this was the selected annotation, update it too
      if (selectedAnnotation && selectedAnnotation.id === editingAnnotation.id) {
        setSelectedAnnotation({ ...selectedAnnotation, ...updates });
      }
      
      // Reset state
      setEditingAnnotation(null);
      setShowEditAnnotationForm(false);
      
      // Show success message
      setSnackbarMessage("Annotation updated successfully");
      setSnackbarOpen(true);
      
    } catch (error) {
      console.error("Failed to update annotation:", error);
      setError("Failed to update annotation. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  
  // Handle selecting a macro image
const handleSelectMacroImage = async (image: MacroImage) => {
    console.log(`Selecting macro image: ${image.id}`);
    
    // Don't reload if the same image is already selected
    if (selectedMacroImage?.id === image.id) {
      console.log('Same image already selected, skipping reload');
      return;
    }
    
    setSelectedMacroImage(image);
    setAnnotationMode(false);
    setCurrentCoordinates(null);
    
    // Reset image dimensions when switching images
    setImageNaturalDimensions(null);
    setImageDisplayDimensions(null);
    
    // Clear selected annotation when switching images
    setSelectedAnnotation(null);
    
    // Load annotations for this image
    setLoading(true);
    try {
      console.log(`Loading annotations for macro image: ${image.id}`);
      const annotationsData = await getMappingAnnotations(image.id);
      console.log("Loaded annotations:", annotationsData);
      
      if (Array.isArray(annotationsData)) {
        setAnnotations(annotationsData);
        console.log(`Successfully loaded ${annotationsData.length} annotations`);
      } else {
        console.warn("Annotations data is not an array:", annotationsData);
        setAnnotations([]);
      }
    } catch (error) {
      console.error("Failed to load annotations:", error);
      setError("Failed to load annotations for this image.");
      setAnnotations([]);
    } finally {
      setLoading(false);
    }
  };  
  // Toggle annotation mode
  const toggleAnnotationMode = () => {
    setAnnotationMode(!annotationMode);
    setCurrentCoordinates(null);
  };
  
  // Calculate image bounds for accurate annotation positioning
  const calculateImageBounds = useCallback((): ImageBounds | null => {
    if (!macroImageRef.current || !imageNaturalDimensions || !imageDisplayDimensions) {
      return null;
    }

    const containerRect = macroImageRef.current.getBoundingClientRect();
    const containerWidth = containerRect.width;
    const containerHeight = containerRect.height;
    
    const containerAspectRatio = containerWidth / containerHeight;
    const imageAspectRatio = imageNaturalDimensions.width / imageNaturalDimensions.height;
    
    let imageDisplayWidth, imageDisplayHeight, imageOffsetX, imageOffsetY;
    
    if (imageAspectRatio > containerAspectRatio) {
      // Image is wider - constrained by width
      imageDisplayWidth = containerWidth;
      imageDisplayHeight = containerWidth / imageAspectRatio;
      imageOffsetX = 0;
      imageOffsetY = (containerHeight - imageDisplayHeight) / 2;
    } else {
      // Image is taller - constrained by height
      imageDisplayWidth = containerHeight * imageAspectRatio;
      imageDisplayHeight = containerHeight;
      imageOffsetX = (containerWidth - imageDisplayWidth) / 2;
      imageOffsetY = 0;
    }
    
    return {
      width: imageDisplayWidth,
      height: imageDisplayHeight,
      offsetX: imageOffsetX,
      offsetY: imageOffsetY,
      containerWidth,
      containerHeight
    };
  }, [imageNaturalDimensions, imageDisplayDimensions]);
  
  // Image load handler to capture dimensions
  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.target as HTMLImageElement;
    setImageNaturalDimensions({
      width: img.naturalWidth,
      height: img.naturalHeight
    });
    
    // Also capture display dimensions
    setImageDisplayDimensions({
      width: img.clientWidth,
      height: img.clientHeight
    });
  };

  // Resize observer for container size changes
  useEffect(() => {
    if (!macroImageRef.current) return;
    
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        // Trigger recalculation when container size changes
        if (imageNaturalDimensions) {
          setImageDisplayDimensions({
            width: entry.contentRect.width,
            height: entry.contentRect.height
          });
        }
      }
    });
    
    resizeObserver.observe(macroImageRef.current);
    
    return () => {
      resizeObserver.disconnect();
    };
  }, [imageNaturalDimensions]);
  
  // Mouse handlers for annotations
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!annotationMode || !macroImageRef.current) return;
    
    const imageBounds = calculateImageBounds();
    if (!imageBounds) return;
    
    const rect = macroImageRef.current.getBoundingClientRect();
    const clientX = e.clientX - rect.left;
    const clientY = e.clientY - rect.top;
    
    // Check if click is within the actual image bounds
    if (clientX < imageBounds.offsetX || clientX > imageBounds.offsetX + imageBounds.width ||
        clientY < imageBounds.offsetY || clientY > imageBounds.offsetY + imageBounds.height) {
      return; // Click is outside the image
    }
    
    // Calculate coordinates relative to the actual image
    const x = (clientX - imageBounds.offsetX) / imageBounds.width;
    const y = (clientY - imageBounds.offsetY) / imageBounds.height;
    
    setCurrentCoordinates({
      x,
      y,
      width: 0,
      height: 0
    });
  };
  
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!annotationMode || !currentCoordinates || !macroImageRef.current) return;
    
    const imageBounds = calculateImageBounds();
    if (!imageBounds) return;
    
    const rect = macroImageRef.current.getBoundingClientRect();
    const clientX = e.clientX - rect.left;
    const clientY = e.clientY - rect.top;
    
    // Calculate coordinates relative to the actual image
    const x = (clientX - imageBounds.offsetX) / imageBounds.width;
    const y = (clientY - imageBounds.offsetY) / imageBounds.height;
    
    // Calculate width and height
    const width = x - currentCoordinates.x;
    const height = y - currentCoordinates.y;
    
    setCurrentCoordinates({
      ...currentCoordinates,
      width,
      height
    });
  };
  
  // Calculate total annotations across all macro images in the session
  const calculateTotalAnnotationsInSession = async (): Promise<number> => {
    if (!selectedMapping) return 0;
    
    try {
      let totalCount = 0;
      
      // Get all macro images for this session
      for (const macroImage of macroImages) {
        const imageAnnotations = await getMappingAnnotations(macroImage.id);
        totalCount += Array.isArray(imageAnnotations) ? imageAnnotations.length : 0;
      }
      
      return totalCount;
    } catch (error) {
      console.error('Failed to calculate total annotations:', error);
      return annotations.length; // Fallback to current image annotations
    }
  };
  
  const handleMouseUp = async () => {
    if (!annotationMode || !currentCoordinates) return;
    
    // If the selection is too small, ignore it
    if (Math.abs(currentCoordinates.width) < 0.01 || Math.abs(currentCoordinates.height) < 0.01) {
      setCurrentCoordinates(null);
      return;
    }
    
    // Normalize coordinates
    const normalized = {
      x: currentCoordinates.width > 0 ? currentCoordinates.x : currentCoordinates.x + currentCoordinates.width,
      y: currentCoordinates.height > 0 ? currentCoordinates.y : currentCoordinates.y + currentCoordinates.height,
      width: Math.abs(currentCoordinates.width),
      height: Math.abs(currentCoordinates.height)
    };
    
    setCurrentCoordinates(normalized);
    
    // Calculate the next annotation number across all macro images in the session
    const totalAnnotationsInSession = await calculateTotalAnnotationsInSession();
    const nextAnnotationNumber = totalAnnotationsInSession + 1;
    
    // Open annotation form dialog with correct numbering
    setAnnotationForm({
      label: `Mole ${nextAnnotationNumber}`,
      orderIndex: totalAnnotationsInSession
    });
    setShowAnnotationForm(true);
  };
  
  // Save new annotation
  const handleSaveAnnotation = async () => {
    if (!selectedMacroImage || !currentCoordinates) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const annotationPayload: AnnotationPayload = {
        label: annotationForm.label,
        x: currentCoordinates.x,
        y: currentCoordinates.y,
        width: currentCoordinates.width,
        height: currentCoordinates.height,
        confidence: 1.0, // Manual annotation is 100% confidence
        orderIndex: annotationForm.orderIndex,
        macroImageId: selectedMacroImage.id
      };
      
      const response = await addAnnotation(annotationPayload);
      
      // Add the new annotation to our list
      if (response && response.annotationId) {
        const newAnnotation: Annotation = {
          id: response.annotationId,
          macroImageId: selectedMacroImage.id,
          label: annotationForm.label,
          x: currentCoordinates.x,
          y: currentCoordinates.y,
          width: currentCoordinates.width,
          height: currentCoordinates.height,
          confidence: 1.0,
          orderIndex: annotationForm.orderIndex,
          linkedMicroId: null,
          linkedMicroUrl: null,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        
        setAnnotations([...annotations, newAnnotation]);
      }
      
      // Reset state
      setCurrentCoordinates(null);
      setShowAnnotationForm(false);
      setAnnotationMode(false);
      
      // Show success message
      setSnackbarMessage("Annotation added successfully");
      setSnackbarOpen(true);
      
    } catch (error) {
      console.error("Failed to save annotation:", error);
      setError("Failed to save annotation. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  
  // Handle annotation selection
  const handleSelectAnnotation = (annotation: Annotation) => {
      resetDermatoscopicState();
    setSelectedAnnotation(annotation);
    setActiveTabIndex(1); // Switch to the dermatoscopic tab
  };

  // Update the existing handleMicroImageUpload function
  const handleMicroImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    // resetDermatoscopicState(); --- Check if this is needed here
  if (!event.target.files || event.target.files.length === 0) return;
  const file = event.target.files[0];
  setReplacementFile(file);
  if (baselineContext) {
    performMicroImageUpload(file);
    return;
  }
  if (selectedAnnotation?.linkedMicroId) {
    setShowReplaceConfirmDialog(true);
  } else {
    performMicroImageUpload(file);
  }
  if (microFileInputRef.current) {
    microFileInputRef.current.value = '';
  }
};

  // Perform the actual micro image upload
// Replace the performMicroImageUpload function with this optimized version:

// Perform the actual micro image upload
const performMicroImageUpload = async (file: File) => {
  const targetAnnotation = baselineContext?.baselineAnnotation || selectedAnnotation;
  if (!targetAnnotation) {
    console.error('No target annotation found for micro image upload');
    return;
  }
  
  console.log('performMicroImageUpload called:', {
    hasBaselineContext: !!baselineContext,
    targetAnnotationLabel: targetAnnotation.label,
    selectedMacroImage: selectedMacroImage?.id,
    selectedMapping: selectedMapping?.id,
    isReplacement: !!baselineContext?.followupAnnotation
  });
  
  setUploadingMicroImage(true);
  setError(null);
  
  try {
    // Upload the micro image
    console.log('Uploading micro image file:', file.name);
    const response = await uploadMicroImage(file);
    console.log('Micro image upload response:', response);
    
    if (response && response.microImageId) {
      if (baselineContext) {
        console.log('Processing baseline context upload');
        
        // Check if we're replacing an existing follow-up annotation or creating a new one
        if (baselineContext.followupAnnotation) {
          console.log('Replacing existing follow-up annotation micro image');
          
          // Update existing follow-up annotation with new micro image
          const updates = {
            linkedMicroId: response.microImageId,
            updatedAt: format_timestamp_for_weaviate()
          };
          
          try {
            await updateAnnotation(baselineContext.followupAnnotation.id, updates);
            console.log('Successfully updated existing follow-up annotation with new micro image');
            
            // Update the baseline context with the new micro image info
            const updatedFollowupAnnotation = {
              ...baselineContext.followupAnnotation,
              linkedMicroId: response.microImageId,
              linkedMicroUrl: response.imageUrl
            };
            
            setBaselineContext({
              ...baselineContext,
              followupAnnotation: updatedFollowupAnnotation
            });
            
            // Update the selected annotation if it matches
            if (selectedAnnotation?.id === baselineContext.followupAnnotation.id) {
              setSelectedAnnotation(updatedFollowupAnnotation);
            }
            
            // Update annotations in the current list
            setAnnotations(prevAnnotations => 
              prevAnnotations.map(ann => 
                ann.id === baselineContext.followupAnnotation!.id 
                  ? updatedFollowupAnnotation 
                  : ann
              )
            );
            
            // Trigger baseline comparison refresh
            setBaselineRefreshTrigger(prev => prev + 1);
            
            setSnackbarMessage("Follow-up dermatoscopic image replaced successfully!");
            setSnackbarOpen(true);
            
          } catch (updateError) {
            console.error("Failed to update existing follow-up annotation:", updateError);
            setError("Failed to replace follow-up image. Please try again.");
          }
          
        } else {
          console.log('Creating new follow-up annotation for baseline reference');
          
          // Create a new annotation in the current follow-up session that references the baseline
          if (selectedMacroImage && selectedMapping) {
            console.log('Creating follow-up annotation for baseline reference');
            
            const followupAnnotationPayload: AnnotationPayload = {
              label: `${baselineContext.baselineAnnotation.label} (Follow-up)`,
              x: baselineContext.baselineAnnotation.x,
              y: baselineContext.baselineAnnotation.y,
              width: baselineContext.baselineAnnotation.width,
              height: baselineContext.baselineAnnotation.height,
              confidence: 1.0,
              orderIndex: annotations.length,
              macroImageId: selectedMacroImage.id
            };
            
            try {
              console.log('Adding follow-up annotation:', followupAnnotationPayload);
              const annotationResponse = await addAnnotation(followupAnnotationPayload);
              console.log('Follow-up annotation response:', annotationResponse);
              
              if (annotationResponse?.annotationId) {
                // Link the new micro image to this new follow-up annotation
                console.log('Linking micro image to follow-up annotation');
                await linkMicroToAnnotation(annotationResponse.annotationId, response.microImageId);
                
                // Create the new annotation object
                const newFollowupAnnotation: Annotation = {
                  id: annotationResponse.annotationId,
                  macroImageId: selectedMacroImage.id,
                  label: followupAnnotationPayload.label,
                  x: followupAnnotationPayload.x,
                  y: followupAnnotationPayload.y,
                  width: followupAnnotationPayload.width,
                  height: followupAnnotationPayload.height,
                  confidence: 1.0,
                  orderIndex: followupAnnotationPayload.orderIndex ?? 0,
                  linkedMicroId: response.microImageId,
                  linkedMicroUrl: response.imageUrl,
                  createdAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString()
                };
                
                console.log('New follow-up annotation created:', newFollowupAnnotation);
                
                // Add to current annotations list instead of full refresh
                setAnnotations(prevAnnotations => [...prevAnnotations, newFollowupAnnotation]);
                
                // Update baseline context with the new follow-up annotation
                setBaselineContext({
                  ...baselineContext,
                  followupAnnotation: newFollowupAnnotation
                });
                
                // Select the new annotation
                setSelectedAnnotation(newFollowupAnnotation);
                
                // Trigger baseline comparison refresh
                setBaselineRefreshTrigger(prev => prev + 1);
                
                setSnackbarMessage("Follow-up dermatoscopic image uploaded and linked successfully!");
                setSnackbarOpen(true);
                
                // Refresh usage after upload
                if (subscription.refreshUsage) {
                  subscription.refreshUsage();
                }
              }
            } catch (annotationError) {
              console.error("Failed to create follow-up annotation:", annotationError);
              setError("Failed to create follow-up annotation. Please try again.");
            }
          } else {
            console.error('Missing selectedMacroImage or selectedMapping for follow-up creation');
            setError("Unable to create follow-up annotation - missing image or session context");
          }
        }
        
      } else {
        console.log('Processing regular annotation upload');
        // Normal annotation linking for current session annotations
        await linkMicroToAnnotation(targetAnnotation.id, response.microImageId);
        
        // Update the annotation in the local state without refetching
        const updatedAnnotation = {
          ...targetAnnotation,
          linkedMicroId: response.microImageId,
          linkedMicroUrl: response.imageUrl,
          updatedAt: new Date().toISOString()
        };
        
        // Update annotations list
        setAnnotations(prevAnnotations => 
          prevAnnotations.map(ann => 
            ann.id === targetAnnotation.id ? updatedAnnotation : ann
          )
        );
        
        // Update selected annotation
        setSelectedAnnotation(updatedAnnotation);
        
        setSnackbarMessage("Dermatoscopic image uploaded and linked successfully");
        setSnackbarOpen(true);
        
        // Refresh usage after upload
        if (subscription.refreshUsage) {
          subscription.refreshUsage();
        }
      }
    } else {
      console.error('No microImageId in upload response:', response);
      setError("Upload failed - no image ID returned");
    }
    
  } catch (error: any) {
    console.error("Failed to upload micro image:", error);
    
    let errorMessage = "Failed to upload dermatoscopic image. Please try again.";
    
    if (error.response?.status === 403) {
      errorMessage = "You've reached the free tier limit. Please upgrade your plan to continue.";
      setError(errorMessage);
      // Refresh usage to show updated count
      if (subscription.refreshUsage) {
        subscription.refreshUsage();
      }
      // Optionally navigate to subscription page after a delay
      setTimeout(() => {
        navigate('/account?tab=subscription');
      }, 2000);
    } else {
      setError(errorMessage);
    }
  } finally {
    setUploadingMicroImage(false);
  }
};

  // Delete an annotation
  const handleDeleteAnnotation = async (annotation: Annotation) => {
    if (!window.confirm("Are you sure you want to delete this annotation?")) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      await deleteAnnotation(annotation.id);
      
      // Remove from our list
      setAnnotations(annotations.filter(a => a.id !== annotation.id));
      
      // If this was the selected annotation, deselect it
      if (selectedAnnotation && selectedAnnotation.id === annotation.id) {
        setSelectedAnnotation(null);
      }
      
      // Show success message
      setSnackbarMessage("Annotation deleted successfully");
      setSnackbarOpen(true);
      
      // Refresh usage after deletion (in case annotation had linked micro image)
      if (subscription.refreshUsage) {
        subscription.refreshUsage();
      }
      
    } catch (error) {
      console.error("Failed to delete annotation:", error);
      setError("Failed to delete annotation. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  
  // Delete a mole mapping session
  const handleDeleteMapping = async (mapping: MoleMappingData) => {
    if (!window.confirm(`Are you sure you want to delete the mole mapping session from ${dayjs(mapping.sessionDate).format('MMMM D, YYYY')}? This will permanently delete all images and annotations.`)) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      await deleteMoleMapping(mapping.id);
      
      // Remove from our list
      const updatedMappings = mappings.filter(m => m.id !== mapping.id);
      setMappings(updatedMappings);
      
      // If this was the selected mapping, select another one or clear selection
      if (selectedMapping && selectedMapping.id === mapping.id) {
        if (updatedMappings.length > 0) {
          const newSelected = updatedMappings[0];
          setSelectedMapping(newSelected);
          await loadMappingData(newSelected.id);
        } else {
          setSelectedMapping(null);
          setMacroImages([]);
          setSelectedMacroImage(null);
          setAnnotations([]);
          setSelectedAnnotation(null);
        }
      }
      
      // Show success message
      setSnackbarMessage("Mole mapping session deleted successfully");
      setSnackbarOpen(true);
      
      // Refresh usage after deletion
      if (subscription.refreshUsage) {
        subscription.refreshUsage();
      }
      
    } catch (error) {
      console.error("Failed to delete mole mapping:", error);
      setError("Failed to delete mole mapping session. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  
  // Macro image replacement functions
  const handleReplaceMacroImageConfirmation = (event: React.ChangeEvent<HTMLInputElement>, macroImage: MacroImage) => {
    if (!event.target.files || event.target.files.length === 0 || !selectedMapping) {
      return;
    }
    
    const file = event.target.files[0];
    setReplacementMacroFile(file);
    setMacroToReplace(macroImage);
    setShowReplaceMacroConfirmDialog(true);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  const handleConfirmMacroReplacement = async () => {
    if (!replacementMacroFile || !macroToReplace || !selectedMapping) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Delete the existing macro image (this will cascade delete annotations)
      console.log(`Deleting macro image: ${macroToReplace.id}`);
      await deleteMacroImage(macroToReplace.id);
      
      // Upload the new image
      const files = [replacementMacroFile];
      const customEvent = {
        target: {
          files: {
            0: replacementMacroFile,
            length: 1,
            item: (index: number) => files[index]
          }
        }
      } as unknown as React.ChangeEvent<HTMLInputElement>;
      
      await handleMacroImageUpload(customEvent);
      
      // Show success message
      setSnackbarMessage("Macro image replaced successfully");
      setSnackbarOpen(true);
      
    } catch (error) {
      console.error("Failed to replace macro image:", error);
      setError("Failed to replace macro image. Please try again.");
    } finally {
      setLoading(false);
      setShowReplaceMacroConfirmDialog(false);
      setReplacementMacroFile(null);
      setMacroToReplace(null);
    }
  };
  
  // Delete a macro image
  const handleDeleteMacroImage = async (macroImage: MacroImage) => {
    if (!window.confirm(`Are you sure you want to delete this macro image? This will also delete all annotations and linked dermatoscopic images.`)) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      await deleteMacroImage(macroImage.id);
      
      // Remove from our list
      const updatedMacroImages = macroImages.filter(img => img.id !== macroImage.id);
      setMacroImages(updatedMacroImages);
      
      // If this was the selected macro image, select another one or clear selection
      if (selectedMacroImage && selectedMacroImage.id === macroImage.id) {
        if (updatedMacroImages.length > 0) {
          const newSelected = updatedMacroImages[0];
          await handleSelectMacroImage(newSelected);
        } else {
          setSelectedMacroImage(null);
          setAnnotations([]);
          setSelectedAnnotation(null);
        }
      }
      
      // Show success message
      setSnackbarMessage("Macro image and associated annotations deleted successfully");
      setSnackbarOpen(true);
      
      // Refresh usage after deletion
      if (subscription.refreshUsage) {
        subscription.refreshUsage();
      }
      
    } catch (error) {
      console.error("Failed to delete macro image:", error);
      setError("Failed to delete macro image. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  
  // Helper function to format timestamps for Weaviate
  const format_timestamp_for_weaviate = (timestamp_str?: string) => {
    const dt = timestamp_str ? new Date(timestamp_str) : new Date();
    return dt.toISOString().replace(/\.\d{3}Z$/, '.000Z');
  };
  
  // Baseline sessions functions
  const loadBaselineSessions = async () => {
    if (!patientId) return;
    
    try {
      console.log(`Loading baseline sessions for patient: ${patientId}`);
      const sessions = await getPatientBaselineSessions(patientId);
      console.log('Loaded baseline sessions:', sessions);
      setBaselineSessions(sessions);
      
      // If we have sessions and none is selected, select the first one
      if (sessions.length > 0 && !selectedBaselineForFollowup) {
        setSelectedBaselineForFollowup(sessions[0].id);
      }
    } catch (error) {
      console.error('Failed to load baseline sessions:', error);
      setBaselineSessions([]);
    }
  };

  useEffect(() => {
    if (patientId) {
      loadBaselineSessions();
    }
  }, [patientId]);

  // Also reload baseline sessions when a new mapping is created
  useEffect(() => {
    if (mappings.length > 0) {
      loadBaselineSessions();
    }
  }, [mappings.length]);

  // Create baseline mole mapping session
  const handleCreateBaselineMapping = async () => {
    // Mark as baseline session
    setLoading(true);
    setError(null);
    
    try {
      const sessionDate = mappingForm.sessionDate || dayjs().format('YYYY-MM-DD');
      const notes = mappingForm.notes || '';
      
      if (!patientId || !userId) {
        throw new Error("Missing patient ID or user ID");
      }
      
      const emptyFileArray: File[] = [];
      
      const response = await createMoleMapping(
        patientId,
        userId,
        sessionDate,
        notes,
        emptyFileArray
      );
      
      const newMapping: MoleMappingData = {
        id: response.mappingId,
        patientId,
        userId,
        sessionDate,
        notes,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      setMappings([newMapping, ...mappings]);
      setSelectedMapping(newMapping);
      setMacroImages([]);
      setSelectedMacroImage(null);
      setAnnotations([]);
      
      setMappingForm({
        sessionDate: dayjs().format('YYYY-MM-DD'),
        notes: ''
      });
      setShowMappingForm(false);
      
      await loadBaselineSessions();
      
      setSnackbarMessage("Baseline mole mapping session created successfully");
      setSnackbarOpen(true);
      
    } catch (error: any) {
      console.error("Failed to create baseline mole mapping:", error);
      setError("Failed to create baseline mole mapping session.");
    } finally {
      setLoading(false);
    }
  };

  // Create follow-up mole mapping session
  const handleCreateFollowupMapping = async () => {
    console.log('Creating follow-up mapping...');
    console.log('Selected baseline:', selectedBaselineForFollowup);
    console.log('Followup form:', followupForm);
    
    if (!selectedBaselineForFollowup) {
      setError("Please select a baseline session");
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const sessionDate = followupForm.sessionDate || dayjs().format('YYYY-MM-DD');
      const notes = followupForm.notes || '';
      
      if (!patientId || !userId) {
        throw new Error("Missing patient ID or user ID");
      }
      
      console.log('Creating follow-up with params:', {
        patientId,
        userId,
        sessionDate,
        selectedBaselineForFollowup,
        notes,
        intervalMonths: followupForm.intervalMonths
      });
      
      const emptyFileArray: File[] = [];
      
      const response = await createFollowupMoleMapping(
        patientId,
        userId,
        sessionDate,
        selectedBaselineForFollowup,
        notes,
        emptyFileArray,
        followupForm.intervalMonths
      );
      
      console.log('Follow-up creation response:', response);
      
      const newMapping: MoleMappingData = {
        id: response.mappingId,
        patientId,
        userId,
        sessionDate,
        notes,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      setMappings([newMapping, ...mappings]);
      setSelectedMapping(newMapping);
      setMacroImages([]);
      setSelectedMacroImage(null);
      setAnnotations([]);
      
      setFollowupForm({
        sessionDate: dayjs().format('YYYY-MM-DD'),
        notes: '',
        intervalMonths: 6
      });
      setSelectedBaselineForFollowup('');
      setShowFollowupDialog(false);
      
      // Refresh mappings to get the updated list
      await fetchMoleMappings();
      
      setSnackbarMessage("Follow-up mole mapping session created successfully");
      setSnackbarOpen(true);
      
    } catch (error: any) {
      console.error("Failed to create follow-up mole mapping:", error);
      console.error("Error details:", error.response?.data);
      
      let errorMessage = "Failed to create follow-up mole mapping session.";
      if (error.response?.data?.detail) {
        errorMessage = `Error: ${error.response.data.detail}`;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoToMacroView = () => {
    setActiveTabIndex(0);
  };


  // Check if current session is a follow-up session - improved logic
  useEffect(() => {
    if (selectedMapping && mappings.length > 0) {
      // Check if this session was created after baseline sessions exist
      // OR if there are baseline sessions available for this patient
      const hasBaselineSessions = baselineSessions.length > 0;
      const isNotOldestSession = mappings.length > 1 && 
        mappings.some(m => new Date(m.createdAt) < new Date(selectedMapping.createdAt));
      
      // Also check if this session has a baselineSessionId (explicit follow-up)
      const hasExplicitBaseline = !!(selectedMapping as any).baselineSessionId;
      
      // A session is a follow-up if:
      // 1. It has baseline sessions available AND it's not the oldest session
      // 2. OR it has an explicit baseline session ID
      // 3. OR if there are baseline sessions and this isn't the very first session created
      const isFollowup = hasExplicitBaseline || 
                        (hasBaselineSessions && isNotOldestSession) ||
                        (hasBaselineSessions && mappings.length > 1);
      
      setIsFollowupSession(isFollowup);
      
      console.log('Follow-up session check:', {
        hasBaselineSessions,
        isNotOldestSession,
        hasExplicitBaseline,
        totalMappings: mappings.length,
        totalBaselines: baselineSessions.length,
        selectedMappingDate: selectedMapping.createdAt,
        isFollowup,
        // Debug: show all mapping dates
        allMappingDates: mappings.map(m => ({id: m.id, date: m.createdAt}))
      });
    } else {
      setIsFollowupSession(false);
    }
  }, [selectedMapping, mappings, baselineSessions]);

  const resetDermatoscopicState = () => {
  setSelectedAnnotation(null);
  setAnnotationMode(false);
  setCurrentCoordinates(null);
  setBaselineContext(null);
  setShowAnnotationForm(false);
  setShowEditAnnotationForm(false);
  setEditingAnnotation(null);
};
  // Handle baseline annotation selection
  const handleSelectBaselineAnnotation = (annotation: Annotation, baselineImage: MacroImage) => {
    if (!selectedMapping) return;
    
    console.log('Setting up baseline context for annotation:', annotation.label);
    
    const context: BaselineAnnotationContext = {
      baselineAnnotation: annotation,
      baselineImage: baselineImage,
      baselineSessionId: baselineImage.mappingId,
      followupSessionId: selectedMapping.id
    };
    
    resetDermatoscopicState(); // 🔁 Clear old state

  resetDermatoscopicState(); // clean state
  setBaselineContext(context);
  setSelectedAnnotation(null); // temporary
  setSelectedMacroImage(baselineImage); // 🩹 🧠 THIS IS CRUCIAL
  setSelectedAnnotation(annotation);
  setActiveTabIndex(1); // Dermatoscopic view
    
    console.log('Baseline context set:', context);
    
    // Show notification to the user
    setSnackbarMessage(`Baseline annotation "${annotation.label}" loaded in dermatoscopic view. You can now add a follow-up image.`);
    setSnackbarOpen(true);
  };

  // Clear baseline context
  const handleClearBaselineContext = () => {
    console.log('Clearing baseline context');

    setBaselineContext(null);
    setSelectedAnnotation(null);
      resetDermatoscopicState();

    setActiveTabIndex(0); // Go back to macroscopic view
  };

  // Handle adding follow-up image to existing annotation - improved
  const handleAddFollowupImage = (annotation: Annotation) => {
    if (!selectedMapping || !selectedMacroImage) return;
    
    console.log('Adding follow-up image for annotation:', annotation.label);
    
    // Create a baseline context for this annotation to enable follow-up mode
    const context: BaselineAnnotationContext = {
      baselineAnnotation: annotation,
      baselineImage: selectedMacroImage,
      baselineSessionId: selectedMacroImage.mappingId,
      followupSessionId: selectedMapping.id
    };
    
    setBaselineContext(context);
    setSelectedAnnotation(annotation);
    setActiveTabIndex(1); // Switch to dermatoscopic tab
    
    setSnackbarMessage(`Follow-up mode activated for annotation: ${annotation.label}. You can now add new dermatoscopic images.`);
    setSnackbarOpen(true);
  };

  // Handle going to dermatoscopic view from baseline
  const handleGoToDermatoscopicFromBaseline = () => {
    setActiveTabIndex(1); // Switch to dermatoscopic tab
  };

  // Perform the actual micro image replacement
  const handleConfirmReplacement = async () => {
    if (!replacementFile || !selectedAnnotation) return;
    
    setUploadingMicroImage(true);
    setError(null);
    
    try {
      // Delete the existing micro image if it exists
      if (selectedAnnotation.linkedMicroId) {
        console.log(`Attempting to delete micro image: ${selectedAnnotation.linkedMicroId}`);
        try {
          await deleteMicroImage(selectedAnnotation.linkedMicroId);
          console.log(`Successfully deleted existing micro image: ${selectedAnnotation.linkedMicroId}`);
          
          // Refresh usage after deletion
          if (subscription.refreshUsage) {
            subscription.refreshUsage();
          }
          
          // Clean the annotation state to remove stale references
          const cleanedAnnotation = {
            ...selectedAnnotation,
            linkedMicroId: null,
            linkedMicroUrl: null
          };
          
          setSelectedAnnotation(cleanedAnnotation);
          setAnnotations(prev => prev.map(ann => 
            ann.id === selectedAnnotation.id ? cleanedAnnotation : ann
          ));
          
          // Allow state to propagate
          await new Promise(resolve => setTimeout(resolve, 100));
          
        } catch (deleteError) {
          console.error("Error deleting existing micro image:", deleteError);
          // Continue with upload even if delete fails - the old image will remain orphaned
          console.log("Continuing with upload despite delete failure");
        }
      }
      
      // Upload the new image
      await performMicroImageUpload(replacementFile);
      
    } catch (error) {
      console.error("Failed to replace micro image:", error);
      setError("Failed to replace dermatoscopic image. Please try again.");
    } finally {
      setUploadingMicroImage(false);
      setShowReplaceConfirmDialog(false);
      setReplacementFile(null);
    }
  };
  // Helper function to get auth headers
  async function getAuthHeaders() {
    const authService = new AuthService();
    const token = await authService.getAccessToken();
    
    if (!token) {
      throw new Error("No access token available");
    }
    
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }
  

  // Test function for SSE - Only add test_event notifications
const testSSE = async () => {
  if (!patientId) return;

  try {
    // 🔐 get the token (same helper used elsewhere)
    const headers = await getAuthHeaders();
    const token = headers.Authorization.split(' ')[1];

    const url = `${API_BASE_URL}/molemapping/test-sse/${patientId}?token=${token}`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (response.ok) {
      console.log('SSE test broadcast sent');
      setSnackbarMessage('✅ SSE test broadcast sent — check Notification Center for updates');
      setSnackbarOpen(true);
    } else {
      console.error('SSE test broadcast failed:', response.status);
      setSnackbarMessage(`❌ SSE test failed (status ${response.status})`);
      setSnackbarOpen(true);
    }
  } catch (error) {
    console.error('Failed to send SSE test:', error);
    setSnackbarMessage('⚠️ Failed to send SSE test broadcast');
    setSnackbarOpen(true);
  }
};

  // Handle SSE events - only show test_event in notifications
  const handleSSEEvent = useCallback((event: any) => {
    console.log('=== SSE EVENT RECEIVED ===');
    console.log('Event type:', event.type);
    console.log('Event data:', event.data);
    console.log('Event timestamp:', event.timestamp);
    console.log('Event patient_id:', event.patient_id);
    console.log('Current patientId:', patientId);
    console.log('==========================');
    
    // Only add notification for test_event - for both broadcaster and receiver
    if (event.type === 'test_event') {
      addNotification({
        type: 'sse',
        title: 'SSE Event: TEST EVENT',
        message: 'Real-time event received from server',
        data: {
          patientId: event.patient_id || patientId,
          timestamp: event.timestamp || new Date().toISOString(),
          eventType: 'TEST_EVENT'
        }
      });
    }
    
    switch (event.type) {
      case 'connected':
        console.log('SSE connected event received for patient:', patientId);
        setSseConnected(true);
        setSnackbarMessage('Live updates connected');
        setSnackbarOpen(true);
        break;
        
      case 'test_event':
        console.log('SSE test event received:', event.data);
        setSnackbarMessage(`SSE test received: ${event.data.message}`);
        setSnackbarOpen(true);
        break;
        
      case 'session_created':
        console.log('New session created, refreshing mappings');
        fetchMoleMappings();
        setSnackbarMessage(`New session created`);
        setSnackbarOpen(true);
        break;
        
      case 'session_updated':
        console.log('Session updated, refreshing current mapping');
        if (selectedMapping && selectedMapping.id === event.data.mapping_id) {
          loadMappingData(selectedMapping.id);
        }
        fetchMoleMappings();
        
        const updateMessage = event.data.update_type === 'images_added' 
          ? `${event.data.new_images_count} new image(s) added to session`
          : 'Session notes updated';
        setSnackbarMessage(updateMessage);
        setSnackbarOpen(true);
        break;
        
      case 'session_deleted':
        console.log('Session deleted, refreshing mappings');
        fetchMoleMappings();
        setSnackbarMessage('Session deleted');
        setSnackbarOpen(true);
        break;
        
      case 'annotation_created':
        console.log('New annotation created, refreshing annotations');
        console.log('Current macro image ID:', selectedMacroImage?.id);
        console.log('Event macro image ID:', event.data.macro_image_id);
        
        if (selectedMacroImage && selectedMacroImage.id === event.data.macro_image_id) {
          console.log('Refreshing annotations for current image');
          getMappingAnnotations(selectedMacroImage.id).then(annotations => {
            console.log('Refreshed annotations after SSE event:', annotations);
            setAnnotations(annotations);
          }).catch(error => {
            console.error('Failed to refresh annotations after SSE event:', error);
          });
        } else {
          console.log('SSE annotation event not for current macro image, skipping refresh');
        }
        
        setSnackbarMessage(`New annotation created: ${event.data.label}`);
        setSnackbarOpen(true);
        break;
        
      case 'annotation_updated':
        console.log('Annotation updated, refreshing annotations');
        if (selectedMacroImage && selectedMacroImage.id === event.data.macro_image_id) {
          getMappingAnnotations(selectedMacroImage.id).then(annotations => {
            console.log('Refreshed annotations after annotation update:', annotations);
            setAnnotations(annotations);
            
            // Update selected annotation if it was the one updated
            if (selectedAnnotation && selectedAnnotation.id === event.data.annotation_id) {
              const updatedAnnotation = annotations.find((a: Annotation) => a.id === event.data.annotation_id);
              if (updatedAnnotation) {
                setSelectedAnnotation(updatedAnnotation);
              }
            }
          }).catch(error => {
            console.error('Failed to refresh annotations after update:', error);
          });
        }
        
        setSnackbarMessage(`Annotation updated: ${event.data.label}`);
        setSnackbarOpen(true);
        break;
        
      case 'annotation_deleted':
        console.log('Annotation deleted, refreshing annotations');
        console.log('Current macro image ID:', selectedMacroImage?.id);
        console.log('Event macro image ID:', event.data.macro_image_id);
        
        if (selectedMacroImage && selectedMacroImage.id === event.data.macro_image_id) {
          console.log('Refreshing annotations for current image after deletion');
          getMappingAnnotations(selectedMacroImage.id).then(annotations => {
            console.log('Refreshed annotations after annotation deletion:', annotations);
            setAnnotations(annotations);
            
            // If the deleted annotation was selected, deselect it
            if (selectedAnnotation && selectedAnnotation.id === event.data.annotation_id) {
              setSelectedAnnotation(null);
              setActiveTabIndex(0); // Go back to macro view
            }
          }).catch(error => {
            console.error('Failed to refresh annotations after deletion:', error);
          });
        }
        
        setSnackbarMessage(`Annotation deleted: ${event.data.label || 'Unknown'}`);
        setSnackbarOpen(true);
        break;
        
      case 'macro_image_uploaded':
        console.log('New macro image uploaded, refreshing macro images');
        if (selectedMapping && selectedMapping.id === event.data.mapping_id) {
          getMacroImages(selectedMapping.id).then(images => {
            console.log('Refreshed macro images after upload:', images);
            setMacroImages(images);
            
            // Optionally select the newly uploaded image
            const newImage = images.find((img: MacroImage) => img.id === event.data.macro_image_id);
            if (newImage) {
              console.log('Auto-selecting newly uploaded image:', newImage.id);
              handleSelectMacroImage(newImage);
            }
          }).catch(error => {
            console.error('Failed to refresh macro images after upload:', error);
          });
        }
        
        setSnackbarMessage(`New macro image uploaded: ${event.data.filename || 'Unknown'}`);
        setSnackbarOpen(true);
        break;
        
      case 'macro_image_deleted':
        console.log('Macro image deleted, refreshing macro images');
        if (selectedMapping) {
          getMacroImages(selectedMapping.id).then(images => {
            console.log('Refreshed macro images after deletion:', images);
            setMacroImages(images);
            
            // If the deleted image was selected, select another or clear
            if (selectedMacroImage && selectedMacroImage.id === event.data.macro_image_id) {
              if (images.length > 0) {
                handleSelectMacroImage(images[0]);
              } else {
                setSelectedMacroImage(null);
                setAnnotations([]);
                setSelectedAnnotation(null);
                setActiveTabIndex(0);
              }
            }
          }).catch(error => {
            console.error('Failed to refresh macro images after deletion:', error);
          });
        }
        
        setSnackbarMessage(`Macro image deleted (${event.data.deleted_annotations} annotations removed)`);
        setSnackbarOpen(true);
        break;
        
      case 'micro_image_linked':
        console.log('Micro image linked to annotation, refreshing data');
        if (selectedMacroImage && selectedMacroImage.id === event.data.macro_image_id) {
          getMappingAnnotations(selectedMacroImage.id).then(annotations => {
            console.log('Refreshed annotations after micro image linking:', annotations);
            setAnnotations(annotations);
            
            // Update selected annotation if it was the one linked
            if (selectedAnnotation && selectedAnnotation.id === event.data.annotation_id) {
              const updatedAnnotation = annotations.find((a: Annotation) => a.id === event.data.annotation_id);
              if (updatedAnnotation) {
                setSelectedAnnotation(updatedAnnotation);
              }
            }
          }).catch(error => {
            console.error('Failed to refresh annotations after micro image linking:', error);
          });
        }
        
        setSnackbarMessage(`Dermatoscopic image linked to: ${event.data.annotation_label}`);
        setSnackbarOpen(true);
        break;
        
      case 'micro_image_deleted':
        console.log('Micro image deleted, refreshing data');
        if (selectedMacroImage && selectedMacroImage.id === event.data.macro_image_id) {
          getMappingAnnotations(selectedMacroImage.id).then(annotations => {
            console.log('Refreshed annotations after micro image deletion:', annotations);
            setAnnotations(annotations);
            
            // Update selected annotation if it was affected
            if (selectedAnnotation && selectedAnnotation.id === event.data.annotation_id) {
              const updatedAnnotation = annotations.find((a: Annotation) => a.id === event.data.annotation_id);
              if ( updatedAnnotation) {
                setSelectedAnnotation(updatedAnnotation);
              }
            }
          }).catch(error => {
            console.error('Failed to refresh annotations after micro image deletion:', error);
          });
        }
        
        const reason = event.data.reason === 'replaced' ? 'replaced' : 'deleted';
        setSnackbarMessage(`Dermatoscopic image ${reason} for: ${event.data.annotation_label}`);
        setSnackbarOpen(true);
        break;
        
      default:
        console.log('Unhandled SSE event type:', event.type, event);
    }
  }, [patientId, selectedMapping?.id, selectedMacroImage?.id, selectedAnnotation?.id, fetchMoleMappings, loadMappingData, handleSelectMacroImage, addNotification]);

  const handleSSEError = useCallback((error: Event) => {
    console.error('SSE connection error:', error);
    setSseConnected(false);
    setSnackbarMessage('Live updates disconnected - attempting to reconnect...');
    setSnackbarOpen(true);
  }, []);

  const handleSSEOpen = useCallback(() => {
    console.log('SSE connection opened - setting connected to true');
    setSseConnected(true); // Also set it here as a backup
  }, []);

  // Initialize SSE connection - stable dependencies
  const sseEnabled = useMemo(() => !!patientId, [patientId]);

  const { connect: connectSSE, disconnect: disconnectSSE } = useSSE({
    patientId: patientId || '',
    enabled: sseEnabled,
    onEvent: handleSSEEvent,
    onError: handleSSEError,
    onOpen: handleSSEOpen
  });

  // Clean up SSE on unmount
  useEffect(() => {
    return () => {
      console.log('Component unmounting, disconnecting SSE');
      disconnectSSE();
    };
  }, [disconnectSSE]);

  // Log SSE connection status changes
  useEffect(() => {
    console.log('SSE connection status changed:', sseConnected);
  }, [sseConnected]);

  // Check if baseline functionality should be available
  const hasAnnotationsWithDermatoscopicImages = annotations.some(annotation => annotation.linkedMicroId);
  const shouldEnableBaselineComparison = baselineSessions.length > 0 || hasAnnotationsWithDermatoscopicImages;

  return (
    <Box sx={{ p: 2 }}>
      {/* Header section */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton
          sx={{ mr: 2 }}
          onClick={() => navigate(`/patients/`)}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" component="h1" sx={{ flexGrow: 1 }}>
          Mole Mapping - {patientName}
          {sseConnected && (
            <Chip 
              size="small" 
              label="Live" 
              color="success" 
              sx={{ ml: 2 }} 
            />
          )}
          {!sseConnected && patientId && (
            <Chip 
              size="small" 
              label="Offline" 
              color="error" 
              sx={{ ml: 2 }} 
            />
          )}
        </Typography>
        
        {/* Enhanced SSE Test Button with tooltip */}
        {patientId && (
          <Button
            variant="outlined"
            onClick={testSSE}
            sx={{ mr: 2 }}
            size="small"
          >
            Live Sync
          </Button>
        )}
        
        {/* Add Patient Report Button */}
        <Button
          variant="outlined"
          startIcon={<ReportIcon />}
          onClick={() => navigate(`/patient-report/${patientId}`)}
          sx={{ ml: 2 }}
        >
          Generate Report
        </Button>
      </Box>
      
      {/* Error message */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {/* Free Tier Usage Indicator */}
      {subscription.isFreeUser && (
        <Paper 
          sx={{ 
            p: 2, 
            mb: 2, 
            bgcolor: subscription.imageCount >= subscription.imageLimit 
              ? 'error.light' 
              : subscription.imageCount >= subscription.imageLimit * 0.8 
                ? 'warning.light' 
                : 'info.light',
            color: 'text.primary'
          }}
          elevation={2}
        >
          <Stack spacing={1.5}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="subtitle2" fontWeight="bold">
                Free Tier Usage
              </Typography>
              <Chip 
                label={`${subscription.imageCount}/${subscription.imageLimit} images`}
                size="small"
                color={
                  subscription.imageCount >= subscription.imageLimit 
                    ? 'error' 
                    : subscription.imageCount >= subscription.imageLimit * 0.8 
                      ? 'warning' 
                      : 'info'
                }
              />
            </Box>
            
            <LinearProgress 
              variant="determinate" 
              value={(subscription.imageCount / subscription.imageLimit) * 100} 
              sx={{ 
                height: 8, 
                borderRadius: 1,
                bgcolor: 'rgba(255, 255, 255, 0.3)',
                '& .MuiLinearProgress-bar': {
                  bgcolor: subscription.imageCount >= subscription.imageLimit 
                    ? 'error.main' 
                    : subscription.imageCount >= subscription.imageLimit * 0.8 
                      ? 'warning.main' 
                      : 'info.main'
                }
              }}
            />
            
            {subscription.imageCount >= subscription.imageLimit && (
              <Alert severity="error" sx={{ mt: 1 }}>
                <Typography variant="body2" gutterBottom>
                  You've reached the free tier limit of {subscription.imageLimit} images. 
                  To continue uploading, please upgrade your plan or delete old cases.
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <Button 
                    size="small" 
                    variant="contained" 
                    color="primary"
                    onClick={() => navigate('/account?tab=subscription')}
                  >
                    Upgrade Plan
                  </Button>
                  <Button 
                    size="small" 
                    variant="outlined"
                    onClick={() => {
                      setSnackbarMessage('Delete old mapping sessions to free up space');
                      setSnackbarOpen(true);
                    }}
                  >
                    Manage Cases
                  </Button>
                </Stack>
              </Alert>
            )}
            
            {subscription.imageCount >= subscription.imageLimit * 0.8 && subscription.imageCount < subscription.imageLimit && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                <Typography variant="body2">
                  You have {subscription.remaining} image{subscription.remaining !== 1 ? 's' : ''} remaining. 
                  Consider upgrading to continue without interruption.
                </Typography>
              </Alert>
            )}
          </Stack>
        </Paper>
      )}
      
      {/* Main content */}
      <Grid container spacing={2}>
        {/* Left sidebar - Mapping sessions */}
        <Grid item xs={12} md={3}>
          <MappingSessionSidebar 
            mappings={mappings}
            selectedMapping={selectedMapping}
            loading={loading}
            baselineSessions={baselineSessions}
            onSelectMapping={(mapping) => {
              setSelectedMapping(mapping);
              loadMappingData(mapping.id);
            }}
            onDeleteMapping={handleDeleteMapping}
            onNewBaseline={() => setShowMappingForm(true)}
            onNewFollowup={() => setShowFollowupDialog(true)}
            onCompare={() => setShowBaselineComparison(true)}
          />
        </Grid>
        
        {/* Main content area */}
        <Grid item xs={12} md={9}>
          {!selectedMapping ? (
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 5 }}>
                <Typography variant="h6" gutterBottom>
                  No Mole Mapping Session Selected
                </Typography>
                <Typography color="text.secondary" paragraph>
                  Please select a session from the sidebar or create a new one to begin.
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => setShowMappingForm(true)}
                >
                  Create New Session
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Show baseline annotation guide when baseline sessions are available OR when current session has dermatoscopic images */}
              <BaselineAnnotationGuide
                isFollowupSession={isFollowupSession}
                hasBaselineSessions={shouldEnableBaselineComparison}
              />

              {/* Tabs for macro/micro/baseline views */}
              <Tabs
                value={activeTabIndex}
                onChange={(_, newValue) => setActiveTabIndex(newValue)}
                sx={{ mb: 2 }}
              >
                <Tab label="Macroscopic" />
                <Tab 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Dermatoscopic
                      {baselineContext && (
                        <Chip size="small" label="Baseline Link" color="info" />
                      )}
                    </Box>
                  } 
                  disabled={!selectedAnnotation && !baselineContext}
                />
                <Tab label="Baseline Comparison" />
              </Tabs>
              
              {/* Macroscopic tab content */}
              {activeTabIndex === 0 && (
                <Box>
                  {/* Macro images gallery with drag & drop */}
                  <MacroImageGallery
                    apiBaseUrl={API_BASE_URL}
                    macroImages={macroImages}
                    selectedMacroImage={selectedMacroImage}
                    isDragActive={isDragActive}
                    fileInputRef={fileInputRef}
                    onSelectImage={handleSelectMacroImage}
                    onUploadImage={handleMacroImageUpload}
                    onReplaceImage={handleReplaceMacroImageConfirmation}
                    onDeleteImage={handleDeleteMacroImage}
                    onOpenCamera={openCamera}
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  />
                  
                  {/* Selected macro image and annotations viewer */}
                  <MacroImageViewer
                    apiBaseUrl={API_BASE_URL}
                    selectedMacroImage={selectedMacroImage}
                    annotationMode={annotationMode}
                    annotations={annotations}
                    currentCoordinates={currentCoordinates}
                    imageBounds={calculateImageBounds()}
                    macroImageRef={macroImageRef}
                    isFollowupSession={isFollowupSession}
                    baselineContext={baselineContext}
                    onToggleAnnotationMode={toggleAnnotationMode}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onImageLoad={handleImageLoad}
                    onSelectAnnotation={handleSelectAnnotation}
                    onEditAnnotation={handleEditAnnotation}
                    onDeleteAnnotation={handleDeleteAnnotation}
                    onAddFollowupImage={handleAddFollowupImage}
                  />
                </Box>
              )}
              
              {/* Dermatoscopic tab content */}
              {activeTabIndex === 1 && (
                <DermatoscopicPanel
                  apiBaseUrl={API_BASE_URL}
                  selectedAnnotation={selectedAnnotation}
                  selectedMacroImage={selectedMacroImage}
                  uploadingMicroImage={uploadingMicroImage}
                  microFileInputRef={microFileInputRef}
                  baselineContext={baselineContext}
                  onImageUpload={handleMicroImageUpload}
                  onOpenCamera={openCamera}
                  onGoToMacroView={handleGoToMacroView}
                  onClearBaselineContext={handleClearBaselineContext}
                />
              )}

            {/* Baseline Comparison tab content */}
              {activeTabIndex === 2 && (
                shouldEnableBaselineComparison ? (
                  <BaselineComparisonPanel
                    apiBaseUrl={API_BASE_URL}
                    baselineSessionId={baselineSessions[0]?.id || selectedMapping.id}
                    followupSessionId={selectedMapping.id}
                    refreshTrigger={baselineRefreshTrigger}
                    onSelectBaselineAnnotation={handleSelectBaselineAnnotation}
                    onGoToDermatoscopic={handleGoToDermatoscopicFromBaseline}
                    currentSessionAsBaseline={baselineSessions.length === 0 && hasAnnotationsWithDermatoscopicImages}
                  />
                ) : (
                  <Card>
                    <CardContent sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="h6" gutterBottom>
                        No Baseline Data Available
                      </Typography>
                      <Typography color="text.secondary" paragraph>
                        To use baseline comparison, you need either:
                        <br />• Existing baseline sessions, OR
                        <br />• Annotations with dermatoscopic images in the current session
                      </Typography>
                      <Button
                        variant="contained"
                        onClick={() => setActiveTabIndex(0)}
                        sx={{ mr: 2 }}
                      >
                        Add Annotations & Images
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => setShowMappingForm(true)}
                      >
                        Create Baseline Session
                      </Button>
                    </CardContent>
                  </Card>
                )
              )}
            </>
          )}
        </Grid>
      </Grid>
      
      {/* Dialogs */}
      <MappingFormDialog
        open={showMappingForm}
        loading={loading}
        mappingForm={mappingForm}
        onClose={() => setShowMappingForm(false)}
        onFormChange={setMappingForm}
        onSubmit={handleCreateMapping}
      />
      
      <FollowupFormDialog
        open={showFollowupDialog}
        loading={loading}
        baselineSessions={baselineSessions}
        selectedBaselineForFollowup={selectedBaselineForFollowup}
        followupForm={followupForm}
        onClose={() => setShowFollowupDialog(false)}
        onBaselineChange={setSelectedBaselineForFollowup}
        onFormChange={setFollowupForm}
        onSubmit={handleCreateFollowupMapping}
      />
      
      <AddAnnotationDialog
        open={showAnnotationForm}
        loading={loading}
        annotationForm={annotationForm}
        onClose={() => {
          setShowAnnotationForm(false);
          setCurrentCoordinates(null);
        }}
        onFormChange={setAnnotationForm}
        onSave={handleSaveAnnotation}
      />
      
      <EditAnnotationDialog
        open={showEditAnnotationForm}
        loading={loading}
        editAnnotationForm={editAnnotationForm}
        onClose={() => {
          setShowEditAnnotationForm(false);
          setEditingAnnotation(null);
        }}
        onFormChange={setEditAnnotationForm}
        onSave={handleSaveEditedAnnotation}
      />
      
      <ReplaceImageDialog
        open={showReplaceConfirmDialog}
        uploading={uploadingMicroImage}
        selectedAnnotation={selectedAnnotation}
        onClose={() => {
          setShowReplaceConfirmDialog(false);
          setReplacementFile(null);
        }}
        onConfirm={handleConfirmReplacement}
      />
      
      <ReplaceMacroDialog
        open={showReplaceMacroConfirmDialog}
        loading={loading}
        onClose={() => {
          setShowReplaceMacroConfirmDialog(false);
          setReplacementMacroFile(null);
          setMacroToReplace(null);
        }}
        onConfirm={handleConfirmMacroReplacement}
      />
      
      {/* Baseline Comparison Dialog */}
      {selectedMapping && (
        <BaselineComparisonDialog
          open={showBaselineComparison}
          onClose={() => setShowBaselineComparison(false)}
          currentSessionId={selectedMapping.id}
          patientId={patientId || ''}
          isFollowupSession={false}
        />
      )}
      
      {/* Camera Dialog */}
      <CameraDialog
        ref={cameraDialogRef}
        open={cameraOpen}
        loading={cameraLoading}
        error={cameraError}
        cameraInitAttempts={cameraInitAttempts}
        availableCameras={availableCameras}
        currentCameraIndex={currentCameraIndex}
        videoRef={videoRef}
        canvasRef={canvasRef}
        onClose={closeCamera}
        onSwitchCamera={switchCamera}
        onCaptureImage={() => captureImage(activeTabIndex === 1 && !!selectedAnnotation)}
      />
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default MoleMappingSpace;

